/**
 * 我的租戶帳單（組織管理員·唯讀）· func_code = ee_my_bills。
 * 只看自己租戶的帳單（後端以 current_user.organization_id 過濾）；頁上方「加 LINE 好友」。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material';

import FunctionPageHeader from '../../../components/FunctionPageHeader';
import TaxProfileCard from './TaxProfileCard';
import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import * as svc from '../../../services/platformBillingService';
import { Bill, BillStatus } from '../../../services/platformBillingService';

const FUNC_CODE = 'ee_my_bills';

const MyBillsPage: React.FC = () => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`platformBilling.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showApiError } = useMessage();

  const [bills, setBills] = useState<Bill[]>([]);
  const [friendUrl, setFriendUrl] = useState<string | null>(null);
  const [attsByBill, setAttsByBill] = useState<Record<number, svc.Attachment[]>>({});
  const [profile, setProfile] = useState<svc.TenantInvoiceProfile | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await svc.getMyBills();
      setBills(data.items);
      setFriendUrl(data.friend_url || null);
      // 發票資料（唯讀·B3）
      try { setProfile(await svc.getMyInvoiceProfile()); }
      catch { /* silent ok: 發票資料載入失敗不擋帳單清單 */ }
      // 各帳單附件（唯讀列出·可下載）
      const pairs = await Promise.all(data.items.map(async (b) => {
        try { return [b.id, await svc.listMyBillAttachments(b.id)] as const; }
        // silent ok: 單張帳單附件載入失敗只讓該張附件為空，不擋整頁帳單清單；逐張跳 toast 反而吵
        catch { return [b.id, [] as svc.Attachment[]] as const; }
      }));
      setAttsByBill(Object.fromEntries(pairs));
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [showApiError]);

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'attachment';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const downloadAtt = async (billId: number, att: svc.Attachment) => {
    try { triggerDownload(await svc.downloadMyBillAttachment(billId, att.id), att.filename); }
    catch (err) { showApiError(err); }
  };

  useEffect(() => {
    if (!permLoading && hasPermission(FUNC_CODE, 'read')) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permLoading]);

  const statusLabel = (s: BillStatus) => tt(`status${s.charAt(0).toUpperCase()}${s.slice(1)}`, s);
  const statusColor = (s: BillStatus): 'default' | 'warning' | 'success' | 'error' =>
    s === 'unpaid' ? 'warning' : s === 'paid' ? 'success' : s === 'void' ? 'error' : 'default';

  if (permLoading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }
  if (!hasPermission(FUNC_CODE, 'read')) {
    return <Box p={3}><Alert severity="warning">{t('common.noPermission', '無此功能存取權限')}</Alert></Box>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <FunctionPageHeader funcCode={FUNC_CODE} />

      {friendUrl && (
        <Alert severity="info" sx={{ mt: 2 }}
          action={<Button color="inherit" size="small" href={friendUrl} target="_blank" rel="noopener">
            {tt('addFriendBtn', '加 LINE 好友')}</Button>}>
          {tt('addFriendHint', '加入「匠耘雲端服務」LINE 好友，帳單通知會同時送到 LINE')}
        </Alert>
      )}

      {profile && (
        <Box sx={{ mt: 2 }}>
          <TaxProfileCard
            profile={profile}
            canEdit={hasPermission(FUNC_CODE, 'update')}
            onSave={svc.saveMyInvoiceProfile}
            onSaved={setProfile}
            readonlyHint={tt('taxProfileReadonlyTenant', '如需修改請洽貴公司組織管理員')}
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {tt('readonlyNote', '此頁唯讀')}
      </Typography>

      {loading ? <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        : bills.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{tt('noBills', '尚無帳單')}</Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {bills.map(b => (
              <Paper key={b.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1">
                      {b.bill_no ? `${b.bill_no}　` : ''}{b.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(b.period_start || '') + (b.period_end ? ` ~ ${b.period_end}` : '')}
                    </Typography>
                  </Box>
                  <Chip size="small" color={statusColor(b.status)} label={statusLabel(b.status)} />
                </Stack>

                <Stack direction="row" spacing={3} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <Typography><b>{tt('amountDue', '應繳總額')}</b>：NT$ {b.amount.toLocaleString()}</Typography>
                  {b.due_date && <Typography><b>{tt('dueDate', '繳費期限')}</b>：{b.due_date}</Typography>}
                  {b.invoice_no && <Typography><b>{tt('invoiceNo', '發票號碼')}</b>：{b.invoice_no}</Typography>}
                </Stack>
                {b.subtotal_amount != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {tt('subtotal', '費用小計（未稅）')} {Number(b.subtotal_amount).toLocaleString()}
                    　{tt('taxBefore', '營業稅 5%')} {Math.round(Number(b.subtotal_amount) * 0.05).toLocaleString()}
                    　{tt('gross', '含稅合計')} {(Number(b.subtotal_amount) + Math.round(Number(b.subtotal_amount) * 0.05)).toLocaleString()}
                    {b.promo_total != null && (
                      `　${tt('promoTotalShort', '優惠價')} ${Number(b.promo_total).toLocaleString()}` +
                      `（${tt('salesShort', '銷售額')} ${Number(b.sales_amount || 0).toLocaleString()}` +
                      `／${tt('taxShort', '稅額')} ${Number(b.tax_amount || 0).toLocaleString()}）`
                    )}
                  </Typography>
                )}

                {b.items.length > 0 && (
                  <Table size="small" sx={{ mt: 1, maxWidth: 420 }}>
                    <TableHead><TableRow>
                      <TableCell>{tt('itemName', '項目')}</TableCell>
                      <TableCell align="right">{tt('itemAmount', '金額')}</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {b.items.map((it, i) => (
                        <TableRow key={i}>
                          <TableCell>{it.name}</TableCell>
                          <TableCell align="right">{Number(it.amount).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {b.status === 'unpaid' && b.remit && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2">
                      <b>{tt('remit', '匯款帳號')}</b>：{b.remit.bank_name}（{b.remit.bank_code}）
                      {b.remit.branch} {b.remit.account_no}／{tt('accountName', '戶名')} {b.remit.account_name}
                    </Typography>
                  </>
                )}

                {(attsByBill[b.id]?.length ?? 0) > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">{tt('attachments', '附件')}</Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {attsByBill[b.id].map(att => (
                        <Stack key={att.id} direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{att.filename}</Typography>
                          <Button size="small" onClick={() => downloadAtt(b.id, att)}>
                            {tt('attDownload', '下載')}</Button>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}

                {b.status === 'paid' && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tt('paidRecord', '付款紀錄')}：NT$ {Number(b.paid_amount || 0).toLocaleString()}
                    {b.paid_at ? `（${new Date(b.paid_at).toLocaleDateString()}）` : ''}
                  </Typography>
                )}
                {b.status === 'void' && b.void_reason && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    {tt('statusVoid', '作廢')}：{b.void_reason}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        )}
    </Box>
  );
};

export default MyBillsPage;
